// src/components/rewards/RewardScreen.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../../services/firebase/config';
import {
  collection, getDocs, doc, getDoc, updateDoc
} from 'firebase/firestore';
import { getIndiaDateString } from '../../services/firebase/firestoreService';
import RewardAnimation from './RewardAnimation';

const RewardScreen = ({ userId, userData, language, onComplete, preloadedData }) => {
  const [reward, setReward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContinue, setShowContinue] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const firstName = userData?.firstName || '';
  const streakCount = userData?.streakCount || 0;
  const village = userData?.village || '';

  const continueText = language === 'mr' ? 'पुढे जा' : 'Continue';

  const saveLastRewardType = useCallback(async (rewardType) => {
    try {
      const todayStr = getIndiaDateString();
      const completionId = `${userId}_${todayStr}`;
      await updateDoc(doc(db, 'fitnessDailyCompletions', completionId), {
        lastRewardType: rewardType
      });
    } catch (e) {}
  }, [userId]);

  const handleContinue = useCallback(async () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    clearTimeout(timerRef.current);
    if (reward) await saveLastRewardType(reward.rewardKey);
    onComplete();
  }, [reward, saveLastRewardType, onComplete]);

  const playAudio = useCallback((audioURL) => {
    if (!audioURL) return;
    try {
      const audio = new Audio(audioURL);
      audio.volume = 0.8;
      audioRef.current = audio;
      audio.play().catch(() => {});
    } catch (e) {}
  }, []);

  const pickReward = useCallback((allDocs, lastRewardType, villageEligible, villagePoints) => {
    const animations = allDocs.filter(d =>
      d.type === 'animation' && d.active === true &&
      ['fireworks', 'confetti'].includes(d.animationId)
    );
    const villageShoutout = allDocs.find(d => d.type === 'village_shoutout' && d.active === true);
    const streakCelebration = allDocs.find(d => d.type === 'streak_celebration' && d.active === true);
    const mediaRewards = allDocs.filter(d => d.type === 'celebrity_meme' && d.active === true);

    const pool = [];
    animations.forEach(a => pool.push({
      type: 'animation', rewardKey: `animation_${a.animationId}`, data: a
    }));
    if (villageEligible && villageShoutout) {
      pool.push({
        type: 'village_shoutout', rewardKey: 'village_shoutout',
        data: { ...villageShoutout, villagePoints }
      });
    }
    if (streakCelebration) {
      pool.push({ type: 'streak_celebration', rewardKey: 'streak_celebration', data: streakCelebration });
    }
    mediaRewards.forEach(m => pool.push({ type: 'celebrity_meme', rewardKey: `meme_${m.id}`, data: m }));

    const eligible = pool.filter(r => !lastRewardType || r.rewardKey !== lastRewardType);
    const finalPool = eligible.length > 0 ? eligible : pool;
    if (finalPool.length === 0) return { type: 'fallback', rewardKey: 'fallback' };
    return finalPool[Math.floor(Math.random() * finalPool.length)];
  }, []);

  useEffect(() => {
    const loadReward = async () => {
      try {
        let allDocs, lastRewardType, villagePoints;
        let villageEligible = true;

        if (preloadedData) {
          ({ allDocs, lastRewardType, villagePoints } = preloadedData);
        } else {
          const snap = await getDocs(collection(db, 'rewardContent'));
          allDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayStr = getIndiaDateString(yesterday);
          const yesterdaySnap = await getDoc(doc(db, 'fitnessDailyCompletions', `${userId}_${yesterdayStr}`));
          lastRewardType = yesterdaySnap.exists() ? yesterdaySnap.data()?.lastRewardType : null;

          villagePoints = 0;
          if (village) {
            const villageSlug = village.toLowerCase().replace(/\s+/g, '_');
            const villageSnap = await getDoc(doc(db, 'villages', villageSlug));
            if (villageSnap.exists()) {
              villagePoints = villageSnap.data().totalPoints || 0;
            }
          }
        }

        setReward(pickReward(allDocs, lastRewardType, villageEligible, villagePoints));
      } catch (e) {
        setReward({ type: 'fallback', rewardKey: 'fallback' });
      } finally {
        setLoading(false);
      }
    };
    loadReward();

    return () => {
      clearTimeout(timerRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [userId, village, preloadedData, pickReward]);

  useEffect(() => {
    if (!reward || reward.type === 'animation' || reward.type === 'fallback') return;
    if (reward.data?.audioURL) playAudio(reward.data.audioURL);
    const timer = setTimeout(() => setShowContinue(true), 5000);
    timerRef.current = timer;
    return () => clearTimeout(timer);
  }, [reward, playAudio]);

  if (loading) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.93)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 2000
      }}>
        <div style={{ color: 'white', fontSize: '40px' }}>🎉</div>
      </div>
    );
  }

  if (reward?.type === 'animation') {
    return (
      <RewardAnimation
        type={reward.data.animationId}
        duration={reward.data.duration || 5}
        name={firstName}
        audioURL={reward.data.audioURL || null}
        language={language}
        isPreview={false}
        onComplete={async () => {
          await saveLastRewardType(reward.rewardKey);
          onComplete();
        }}
      />
    );
  }

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: '24px', textAlign: 'center'
  };

  const continueBtn = {
    marginTop: '32px', background: '#007fff', color: 'white',
    border: 'none', padding: 'clamp(12px,3vw,16px) clamp(28px,6vw,48px)',
    borderRadius: '14px', fontSize: 'clamp(15px,3vw,18px)',
    fontWeight: 700, cursor: 'pointer'
  };

  if (reward?.type === 'village_shoutout') {
    const message = (reward.data.message || '')
      .replace('[Name]', firstName)
      .replace('[Village]', village)
      .replace('[Points]', reward.data.villagePoints || 0);
    return (
      <div style={{ ...overlayStyle, background: 'linear-gradient(160deg, #0f4c2a 0%, #1a7a42 50%, #0a2e18 100%)' }}>
        <div style={{ maxWidth: '320px', width: '100%', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ color: 'white', fontSize: 'clamp(20px,5vw,30px)',
            fontWeight: 800, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {message}
          </div>
        </div>
        {showContinue && <button style={continueBtn} onClick={handleContinue}>{continueText}</button>}
      </div>
    );
  }

  if (reward?.type === 'streak_celebration') {
    const message = (reward.data.message || '')
      .replace('[Name]', firstName)
      .replace('[Streak]', streakCount);
    return (
      <div style={{ ...overlayStyle, background: 'linear-gradient(160deg, #7c2d00 0%, #c2410c 50%, #431407 100%)' }}>
        <div style={{ maxWidth: '320px', width: '100%', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ color: 'white', fontSize: 'clamp(20px,5vw,30px)',
            fontWeight: 800, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {message}
          </div>
        </div>
        {showContinue && <button style={continueBtn} onClick={handleContinue}>{continueText}</button>}
      </div>
    );
  }

  if (reward?.type === 'celebrity_meme') {
    return (
      <div style={{ ...overlayStyle, background: 'rgba(0,0,0,0.93)' }}>
        <div style={{ position: 'relative', maxWidth: '320px', width: '100%',
          borderRadius: '16px', overflow: 'hidden' }}>
          <img src={reward.data.imageURL} alt="reward"
            style={{ width: '100%', display: 'block', borderRadius: '16px' }} />
          {reward.data.dialogueText && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
              padding: '20px 16px 16px', borderRadius: '0 0 16px 16px' }}>
              <div style={{ color: '#fff', fontSize: 'clamp(16px,4vw,22px)', fontWeight: 800 }}>
                {reward.data.dialogueText}
              </div>
            </div>
          )}
        </div>
        {showContinue && <button style={continueBtn} onClick={handleContinue}>{continueText}</button>}
      </div>
    );
  }

  return (
    <div style={{ ...overlayStyle, background: 'rgba(0,0,0,0.93)' }}>
      <div style={{ fontSize: 'clamp(50px,12vw,80px)', marginBottom: '16px' }}>🎉</div>
      <div style={{ color: 'white', fontSize: 'clamp(18px,5vw,28px)',
        fontWeight: 800, lineHeight: 1.4 }}>
        {language === 'mr'
          ? `${firstName}, आजची सर्व कामे पूर्ण केलीस!`
          : `${firstName}, you finished all tasks today!`}
      </div>
      <button style={continueBtn} onClick={handleContinue}>{continueText}</button>
    </div>
  );
};

export default RewardScreen;